import * as React from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import InformationTooltip from 'modules/core/components/Inputs/InformationTooltip'
import Select from 'modules/core/components/Inputs/Select'
import Text from 'modules/core/components/Inputs/Text'
import Modal from 'modules/core/components/Modal'
import MultiInput from 'modules/core/components/MultiInput'
import i18n from 'modules/core/lib/i18n'

import type PeriodForm from 'modules/timeoff/lib/PeriodForm'
import TenurePeriod from 'modules/timeoff/lib/TenurePeriod'
import { tenureBalanceTypes, tenurePeriodTypes } from 'modules/timeoff/lib/dynamicTranslations'

interface Props {
  periodForm: PeriodForm
  closeModal: () => void
  onSave: (...args: any[]) => any
  baseAllowance: number
  ptoTenuresEnabled: boolean
  isHourly: boolean
  isNew?: boolean
}

const FormModal = ({
  periodForm,
  closeModal,
  onSave,
  baseAllowance,
  ptoTenuresEnabled,
  isHourly,
  isNew = false,
}: Props) => {
  const tenurePeriodTypesOptions = Object.keys(TenurePeriod.TENURE_PERIOD_TYPE).map((value) => ({
    label: tenurePeriodTypes(value),
    value,
  }))

  const tenureBalanceTypesOptions = Object.keys(TenurePeriod.TENURE_BALANCE_TYPE).map((value) => ({
    label: tenureBalanceTypes(value),
    value,
  }))

  const title = isNew
    ? i18n.t('time_off.allowances.modal.tenure_period.new')
    : i18n.t('time_off.allowances.modal.tenure_period.edit')

  const description = isHourly
    ? i18n.t('time_off.allowances.modal.tenure_period.description_hours')
    : i18n.t('time_off.allowances.modal.tenure_period.description_days')

  const adjustmentLabel = isHourly
    ? i18n.t('time_off.allowances.page.labels.tenure_adjustment_hours')
    : i18n.t('time_off.allowances.page.labels.tenure_adjustment_days')

  const adjustmentDescription = isHourly
    ? i18n.t('time_off.allowances.page.description.tenure_adjustment_hours')
    : i18n.t('time_off.allowances.page.description.tenure_adjustment_days')

  const adjustmentInformation = isHourly
    ? i18n.t('time_off.allowances.page.information.tenure_adjustment_hours')
    : i18n.t('time_off.allowances.page.information.tenure_adjustment_days')

  const MaxBalanceCapLabel = isHourly
    ? i18n.t('time_off.allowances.page.labels.max_cap_hours')
    : i18n.t('time_off.allowances.page.labels.max_cap_days')

  const MaxBalanceCapInfo = i18n.t('time_off.allowances.page.information.max_cap')
  const MaxBalanceCapDescription = i18n.t('time_off.allowances.page.description.max_cap')

  const tenureTypeDescription = i18n.t('time_off.allowances.modal.tenure_period.balance_type')
  const hoursAccruedDescription = i18n.t('time_off.allowances.page.labels.accrued_factor_in_cents')
  const perHoursWorkedDescription = i18n.t(
    'time_off.allowances.page.labels.accrued_denominator_in_cents'
  )

  const saveButton = isNew
    ? i18n.t('time_off.allowances.modal.tenure_period.create')
    : i18n.t('time_off.allowances.modal.tenure_period.update')

  const adjustment = parseFloat(periodForm.get('adjustment_in_cents').value)

  const totalDays = (adjustment || 0) + baseAllowance

  const fixedBalanceSelected =
    periodForm.get('balance_type').value === TenurePeriod.TENURE_BALANCE_TYPE.fixed_balance
  const workedTimeBasedSelected =
    periodForm.get('balance_type').value === TenurePeriod.TENURE_BALANCE_TYPE.time_worked_based

  const isDisabled = () => {
    if (periodForm.get('balance_type').value === TenurePeriod.TENURE_BALANCE_TYPE.fixed_balance) {
      return (
        !periodForm.get('period_length').value ||
        !periodForm.get('period_type').value ||
        !periodForm.get('adjustment_in_cents').value
      )
    } else {
      return (
        !periodForm.get('period_length').value ||
        !periodForm.get('period_type').value ||
        !periodForm.get('time_worked_based_hours_accrued_in_cents').value ||
        !periodForm.get('time_worked_based_per_hours_worked_in_cents').value
      )
    }
  }

  const onConfirm = () => onSave(periodForm.data())

  const fixedOptions = {
    count: <b>{totalDays}</b>,
  }

  return (
    <Modal
      illustration="document-edit"
      title={title}
      description={description}
      onClose={closeModal}
      footer={
        <>
          <Button onClick={closeModal} type="secondary">
            {i18n.t('common.cancel')}
          </Button>
          <Button disabled={isDisabled()} onClick={onConfirm}>
            {saveButton}
          </Button>
        </>
      }
    >
      <Box gap="s16">
        <MultiInput
          label={i18n.t('time_off.allowances.page.labels.tenure_period_length')}
          secondaryLabel={
            <InformationTooltip
              information={i18n.t('time_off.allowances.page.information.tenure_period_length')}
              icon="question"
            />
          }
          description={i18n.t('time_off.allowances.page.description.tenure_period_length_hours')}
        >
          <Text // TODO: missing hidden label
            field={periodForm.get('period_length')}
            type="number"
            mandatory
          />
          <Select // TODO: missing hidden label
            field={periodForm.get('period_type')}
            options={tenurePeriodTypesOptions}
          />
        </MultiInput>
        {ptoTenuresEnabled && (
          <Select
            label={tenureTypeDescription}
            field={periodForm.get('balance_type')}
            options={tenureBalanceTypesOptions}
          />
        )}
        {ptoTenuresEnabled && workedTimeBasedSelected && (
          <MultiInput>
            <Text
              label={hoursAccruedDescription}
              field={periodForm.get('time_worked_based_hours_accrued_in_cents')}
              type="number"
              mandatory={workedTimeBasedSelected}
            />
            <Text
              label={perHoursWorkedDescription}
              field={periodForm.get('time_worked_based_per_hours_worked_in_cents')}
              type="number"
              mandatory={workedTimeBasedSelected}
            />
          </MultiInput>
        )}
        {fixedBalanceSelected && (
          <Text
            label={adjustmentLabel}
            secondaryLabel={
              <InformationTooltip information={adjustmentInformation} icon="question" />
            }
            description={adjustmentDescription}
            field={periodForm.get('adjustment_in_cents')}
            type="number"
            mandatory={fixedBalanceSelected}
          />
        )}
        <Text
          label={MaxBalanceCapLabel}
          secondaryLabel={<InformationTooltip information={MaxBalanceCapInfo} icon="question" />}
          description={MaxBalanceCapDescription}
          placeholder={i18n.t('common.unlimited')}
          field={periodForm.get('max_cap_in_cents')}
          type="number"
        />
        <Box
          paddingX="s24"
          paddingY="s16"
          background="grey200"
          borderRadius={{
            all: 'abs008',
          }}
        >
          {fixedBalanceSelected &&
            (isHourly
              ? i18n.tView('time_off.allowances.modal.tenure_period.footer_hours', fixedOptions)
              : i18n.tView('time_off.allowances.modal.tenure_period.footer_days', fixedOptions))}
          {workedTimeBasedSelected &&
            i18n.tView('time_off.allowances.modal.tenure_period.footer_pto', {
              accrued_hours: (
                <b>
                  {parseFloat(periodForm.get('time_worked_based_hours_accrued_in_cents').value) ||
                    'X'}
                </b>
              ),

              per_hours_worked: (
                <b>
                  {parseFloat(
                    periodForm.get('time_worked_based_per_hours_worked_in_cents').value
                  ) || 'X'}
                </b>
              ),
            })}
        </Box>
      </Box>
    </Modal>
  )
}

export default observer(FormModal)
